"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { type RefObject } from "react";
import { useInfiniteListAdapter } from "@/react-query/adapters/use-infinite-query-adapter";
import { apiClient } from "@/react-query/api-client";
import { API_ROUTES } from "@/react-query/api-routes";
import type { ApiEnvelope } from "@/react-query/api-types";
import { CACHE_TIME } from "@/react-query/cache-times";
import { queryKeys } from "@/react-query/query-keys";
export type TargetSource = "CONTRACT" | "PERSONAL_BEST" | "DEFAULT";
export type StreamStatsProgressResponse = {
  airtimeTargetSource?: TargetSource;
  airtimeTargetMinutes?: number;
  airtimeCurrentMinutes: number;
  airtimeProgress?: number | null;
  airtimePersonalBestMinutes?: number;
  sovTargetSource?: TargetSource;
  sovTarget?: number;
  sovCurrent: number;
  sovPersonalBest?: number;
  viewingCurrentMonth?: boolean;
};
export type DailyStats = {
  date: string;
  airtimeMinutes: number;
  accumulatedAirtime: number;
  reach: number;
  averageView: number;
  gamesPlayed: number;
  peakViewers: number;
  trueAirtimeMinutes: number;
  trueReach: number;
  totalAirtimeMinutes: number;
  totalReach: number;
  totalTrueReach: number;
  shareOfReach: number;
  shareOfAirtime: number;
  accShareOfAirtime: number;
};
export type HourlyStats = DailyStats & {
  hour: string;
};
export type StreamStatsSummaryResponse = {
  dailyStats: DailyStats[];
  hourlyStats?: HourlyStats[];
  summary: {
    totalAirtimeMinutes: number;
    totalReach: number;
    averageView: number;
    totalGamesPlayed: number;
    peakViewers: number;
    totalTrueAirtimeMinutes: number;
    totalTrueReach: number;
    allProviderAirtimeMinutes: number;
    allProviderReach: number;
    allProviderTrueReach: number;
  };
  sov: {
    shareOfAirtime: number;
    shareOfReach: number;
  };
};
export type StreamStatsGame = {
  gameId: number;
  gameName: string;
  displayImage: string;
  airtimeMinutes: number;
  avgViewers: number;
  peakViewers: number;
  trueAirtimeMinutes: number;
  trueAvgViewers: number;
  reach: number;
  trueReach?: number;
  shareOfAirtime: number;
  shareOfReach: number;
  shareOfAverageView: number;
  shareOfPeakView: number;
  relativeAirtime: number;
  relativeReach: number;
  relativeAverageView: number;
  relativePeakView: number;
  delta: {
    airtimeChange: number;
    reachChange: number;
    trueReachChange: number;
  };
};
export type StreamStatsGamesResponse = {
  content: StreamStatsGame[];
  totalElements: number;
  page: number;
  size: number;
};
export type StreamStatsProgressParams = {
  profileId?: string;
  startDate?: string;
  endDate?: string;
};
export type StreamStatsGamesParams = StreamStatsProgressParams & {
  search?: string;
  sort?: string;
  page?: number;
  size?: number;
};
type QueryOptions = {
  enabled?: boolean;
};
const streamStatsParams = ({
  profileId,
  startDate,
  endDate
}: StreamStatsProgressParams) => ({
  ...(profileId ? {
    profileId
  } : {}),
  ...(startDate ? {
    from: startDate
  } : {}),
  ...(endDate ? {
    to: endDate
  } : {})
});
export function useStreamStatsProgressQuery(t0, t1) {
  const $ = _c(12);
  let t2;
  if ($[0] !== t0) {
    t2 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const filters = t2;
  let t3;
  if ($[2] !== t1) {
    t3 = t1 === undefined ? {} : t1;
    $[2] = t1;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const options = t3;
  let t4;
  if ($[4] !== filters) {
    t4 = queryKeys.streamStatsProgress(filters);
    $[4] = filters;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== filters) {
    t5 = async () => {
      const response = await apiClient(API_ROUTES.statisticsProgress, {
        params: streamStatsParams(filters)
      });
      return response.data;
    };
    $[6] = filters;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = options.enabled ?? true;
  let t7;
  if ($[8] !== t4 || $[9] !== t5 || $[10] !== t6) {
    t7 = {
      queryKey: t4,
      queryFn: t5,
      enabled: t6,
      staleTime: CACHE_TIME.BALANCED,
      gcTime: CACHE_TIME.PERSISTENT,
      refetchOnWindowFocus: false
    };
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return useQuery(t7);
}
export function useStreamStatsSummaryQuery(t0, t1) {
  const $ = _c(12);
  let t2;
  if ($[0] !== t0) {
    t2 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const filters = t2;
  let t3;
  if ($[2] !== t1) {
    t3 = t1 === undefined ? {} : t1;
    $[2] = t1;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const options = t3;
  let t4;
  if ($[4] !== filters) {
    t4 = queryKeys.streamStatsSummary(filters);
    $[4] = filters;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== filters) {
    t5 = async () => {
      const response = await apiClient(API_ROUTES.statisticsPersonalized, {
        params: streamStatsParams(filters)
      });
      return response.data;
    };
    $[6] = filters;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = options.enabled ?? true;
  let t7;
  if ($[8] !== t4 || $[9] !== t5 || $[10] !== t6) {
    t7 = {
      queryKey: t4,
      queryFn: t5,
      enabled: t6,
      staleTime: CACHE_TIME.BALANCED,
      gcTime: CACHE_TIME.PERSISTENT,
      refetchOnWindowFocus: false
    };
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return useQuery(t7);
}
export function useStreamStatsGamesQuery(t0, t1) {
  const $ = _c(12);
  let t2;
  if ($[0] !== t0) {
    t2 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const filters = t2;
  let t3;
  if ($[2] !== t1) {
    t3 = t1 === undefined ? {} : t1;
    $[2] = t1;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const options = t3;
  let t4;
  if ($[4] !== filters) {
    t4 = queryKeys.streamStatsGames(filters);
    $[4] = filters;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== filters) {
    t5 = async () => {
      const {
        search,
        sort,
        page: t6,
        size: t7,
        ...dateFilters
      } = filters;
      const page = t6 === undefined ? 0 : t6;
      const size = t7 === undefined ? 20 : t7;
      const response = await apiClient(API_ROUTES.statisticsPersonalizedGames, {
        params: {
          ...(search ? {
            search
          } : {}),
          ...(sort ? {
            sort
          } : {}),
          ...streamStatsParams(dateFilters),
          page,
          size
        }
      });
      const data = response.data;
      return {
        ...data,
        hasMore: (data.page + 1) * data.size < data.totalElements
      };
    };
    $[6] = filters;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = options.enabled ?? true;
  let t7;
  if ($[8] !== t4 || $[9] !== t5 || $[10] !== t6) {
    t7 = {
      queryKey: t4,
      queryFn: t5,
      enabled: t6,
      staleTime: CACHE_TIME.BALANCED,
      gcTime: CACHE_TIME.PERSISTENT,
      refetchOnWindowFocus: false
    };
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return useQuery(t7);
}
export function useInfiniteStreamStatsGames(t0) {
  const $ = _c(25);
  let filters;
  let scrollContainerRef;
  let skip;
  let t1;
  let t2;
  if ($[0] !== t0) {
    const {
      skip: t3,
      scrollContainerRef: t4,
      ...t5
    } = t0;
    scrollContainerRef = t4;
    filters = t5;
    skip = t3 === undefined ? false : t3;
    t2 = useInfiniteListAdapter;
    t1 = queryKeys.infiniteStreamStatsGames(filters);
    $[0] = t0;
    $[1] = filters;
    $[2] = scrollContainerRef;
    $[3] = skip;
    $[4] = t1;
    $[5] = t2;
  } else {
    filters = $[1];
    scrollContainerRef = $[2];
    skip = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const t3 = !skip;
  let t4;
  if ($[6] !== scrollContainerRef) {
    t4 = {
      containerRef: scrollContainerRef
    };
    $[6] = scrollContainerRef;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== filters || $[9] !== t1 || $[10] !== t3 || $[11] !== t4) {
    t5 = {
      queryKey: t1,
      params: filters,
      enabled: t3,
      staleTime: CACHE_TIME.BALANCED,
      gcTime: CACHE_TIME.PERSISTENT,
      refetchOnWindowFocus: false,
      fetchPage: _temp,
      getNextPage: _temp2,
      selectItems: _temp3,
      scroll: t4
    };
    $[8] = filters;
    $[9] = t1;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const result = t2(t5);
  const t6 = result.data?.pages[0]?.totalElements ?? 0;
  const t7 = !skip && result.isLoading;
  let t8;
  if ($[13] !== result) {
    t8 = () => result.refetch();
    $[13] = result;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== result.hasMore || $[16] !== result.isError || $[17] !== result.isFetching || $[18] !== result.items || $[19] !== result.loadMore || $[20] !== result.loadMoreRef || $[21] !== t6 || $[22] !== t7 || $[23] !== t8) {
    t9 = {
      games: result.items,
      totalElements: t6,
      isLoading: t7,
      isFetching: result.isFetching,
      isError: result.isError,
      hasMore: result.hasMore,
      loadMoreRef: result.loadMoreRef,
      loadMore: result.loadMore,
      reset: t8
    };
    $[15] = result.hasMore;
    $[16] = result.isError;
    $[17] = result.isFetching;
    $[18] = result.items;
    $[19] = result.loadMore;
    $[20] = result.loadMoreRef;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}
function _temp3(page_0) {
  return page_0.content;
}
function _temp2(lastPage) {
  return lastPage.hasMore ? lastPage.page + 1 : undefined;
}
async function _temp(params, page) {
  const response = await apiClient(API_ROUTES.statisticsPersonalizedGames, {
    params: {
      ...(params.search ? {
        search: params.search
      } : {}),
      ...(params.sort ? {
        sort: params.sort
      } : {}),
      ...streamStatsParams(params),
      page,
      size: params.size ?? 20
    }
  });
  const data = response.data;
  return {
    ...data,
    hasMore: (data.page + 1) * data.size < data.totalElements
  };
}